import { Shield, Lock, ImageIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface MediaConsentSheetProps {
  isOpen: boolean;
  onAllow: () => void;
  onDismiss: () => void;
  isLoading?: boolean;
}

export function MediaConsentSheet({ isOpen, onAllow, onDismiss, isLoading }: MediaConsentSheetProps) {
  const { t } = useTranslation("common");

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm"
            onClick={onDismiss}
          />
          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 400 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-3xl border-t border-border/50 shadow-elevated safe-bottom"
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full bg-muted-foreground/20" />
            </div>

            <div className="px-6 pb-8 pt-2">
              {/* Icon */}
              <div className="flex justify-center mb-5">
                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <ImageIcon className="h-8 w-8 text-primary" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-foreground text-center mb-2">
                {t("sendPhotosFiles")}
              </h3>

              {/* Explainer items */}
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <Lock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <p className="text-[13px] text-muted-foreground leading-relaxed">
                    {t("mediaEncryptedExplainer")}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <p className="text-[13px] text-muted-foreground leading-relaxed">
                    {t("filesStoredSecurely")}
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="space-y-2.5">
                <Button
                  className="w-full rounded-2xl h-12 text-[15px] font-medium"
                  onClick={onAllow}
                  disabled={isLoading}
                >
                  {isLoading ? t("enabling") : t("allow")}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full rounded-2xl h-12 text-[15px] font-medium text-muted-foreground"
                  onClick={onDismiss}
                  disabled={isLoading}
                >
                  {t("notNow")}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
